import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, useMotionValue, useSpring, useTransform, animate } from "framer-motion";
import { Lock, Sparkles, Layers, X } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ArenaProfile } from "@/components/ArenaBits";
import { CardRoleBadge } from "@/components/CardRoleBadge";
import { CardLifecycleBadge } from "@/components/CardLifecycleBadge";
import { LockedCardArtwork } from "@/components/LockedCardArtwork";
import { GearSlots } from "@/components/GearSlots";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const RARITY_CLASS = {
  common: "rarity-common",
  uncommon: "rarity-uncommon",
  rare: "rarity-rare",
  epic: "rarity-epic",
  legendary: "rarity-legendary",
  goddess: "rarity-goddess",
};

function SparkleLayer({ count = 14 }) {
  // Memoize positions so they don't change every render
  const [sparkles] = useState(() => Array.from({ length: count }).map(() => ({
    id: crypto.randomUUID(),
    top: Math.random() * 100,
    left: Math.random() * 100,
    delay: Math.random() * 2,
    size: 2 + Math.random() * 4,
  })));
  return (
    <div className="sparkle-layer">
      {sparkles.map((s, i) => (
        <span
          key={s.id}
          className="sparkle"
          style={{ top: `${s.top}%`, left: `${s.left}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }}
        />
      ))}
    </div>
  );
}

// Build the object-fit / object-position / scale style for a card's artwork
// based on the admin-chosen framing. Optionally append a CSS filter (used for
// the locked preview). This is a STATIC style (no hover behavior).
export function cardImageStyle(card, extraFilter) {
  const style = {
    objectFit: card?.image_fit || "cover",
    objectPosition: `${card?.image_pos_x ?? 50}% ${card?.image_pos_y ?? 50}%`,
    transform: `scale(${card?.image_scale ?? 1})`,
  };
  if (extraFilter) style.filter = extraFilter;
  return style;
}

// CSS variables consumed by the `.peek-framed` class. Lets the artwork respect
// the admin framing at rest while still zooming out to reveal the full image
// on hover (handled entirely in CSS, so hover can override these).
export function cardImageVars(card) {
  return {
    "--card-fit": card?.image_fit || "cover",
    "--card-pos": `${card?.image_pos_x ?? 50}% ${card?.image_pos_y ?? 50}%`,
    "--card-scale": card?.image_scale ?? 1,
  };
}

function LockedFront({ card }) {
  return (
    <div className="absolute inset-0 rounded-[inherit] overflow-hidden card-face bg-black">
      <LockedCardArtwork card={card} view="front" />
    </div>
  );
}

function CardFront({ card }) {
  return (
    <div className="absolute inset-0 rounded-[inherit] overflow-hidden card-face bg-black">
      {card.front_image ? (
        <img
          src={card.front_image}
          alt={card.title}
          className="absolute inset-0 w-full h-full peek-framed"
          style={cardImageVars(card)}
          draggable={false}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/30">No image</div>
      )}
      <div className="foil-overlay peek-fade" />
      <div className="glare-layer peek-fade" />
      {card.rarity === "goddess" && <div className="peek-fade"><SparkleLayer count={16} /></div>}
      <div className="absolute inset-0 holo-scan peek-fade" />

      {/* HUD frame */}
      <div className="absolute inset-0 pointer-events-none peek-fade">
        <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2" style={{ borderColor: "var(--edge)" }} />
        <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2" style={{ borderColor: "var(--edge)" }} />
        <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2" style={{ borderColor: "var(--edge)" }} />
        <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2" style={{ borderColor: "var(--edge)" }} />
      </div>

      <div className="absolute top-2 left-3 right-3 flex items-start justify-between peek-fade">
        <div className="font-display text-base text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)] max-w-[70%]" data-testid="card-title">
          {card.title}
        </div>
        <div
          data-testid="card-rarity-badge"
          className="font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 rounded-md backdrop-blur-sm"
          style={{
            color: "var(--label)",
            background: "rgba(0,0,0,0.45)",
            border: "1px solid var(--edge)",
          }}
        >
          {card.rarity}
        </div>
      </div>

      <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between gap-1 peek-fade">
        <div className="font-mono text-[10px] tracking-[0.2em] text-white/85 px-2 py-1 rounded backdrop-blur-sm bg-black/50" data-testid="card-serial-number">
          {card.serial}
        </div>
        {card.role_key && (
          <CardRoleBadge card={card} size="sm" className="shrink-0" />
        )}
        {card.edition_date && (
          <div className="font-mono text-[9px] text-cyan-200 px-1.5 py-0.5 rounded backdrop-blur-sm bg-black/50 shrink-0" data-testid="card-edition-date">
            {card.edition_date}
          </div>
        )}
        {card.rarity === "goddess" && <Sparkles className="h-4 w-4 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />}
      </div>
    </div>
  );
}

function CardBack({ card }) {
  // Default back: Playden logo. If template has back_image use it.
  // For visual variety across editions, alternate light/dark by edition_number (deterministic per edition).
  const editionNumber = card.edition_number || 1;
  const useLight = editionNumber % 2 === 0;
  const defaultBack = useLight ? "/brand/playden-white.png" : "/brand/playden-black.png";
  const backSrc = card.back_image || defaultBack;
  return (
    <div className="absolute inset-0 rounded-[inherit] overflow-hidden card-face card-back bg-black">
      <img src={backSrc} alt="back" className={`absolute inset-0 w-full h-full peek-img ${card.back_image ? "object-cover" : "object-contain p-4"}`} draggable={false} />
      <div className="foil-overlay peek-fade" />
      <div className="glare-layer peek-fade" />
      <div className="absolute inset-0 holo-scan peek-fade" />
    </div>
  );
}

export function Card3D({ card, size = "md", interactive = true, alwaysOwner = false, hoverStats = true, previewSide = "right" }) {
  const isLocked = card.locked && !alwaysOwner;
  const wrapperRef = useRef(null);
  const [isFlipped, setIsFlipped] = useState(false);
  // Touch devices have no hover, so a single tap emulates "hover" (holo reveal)
  // and a double tap flips the card.
  const [mobileActive, setMobileActive] = useState(false);
  const [showHint, setShowHint] = useState(
    () => typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(hover: none) and (pointer: coarse)").matches
      : false
  );
  const tapRef = useRef({ lastTap: 0, timer: null });
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, lastX: 0, dragged: false });

  // Flip motion value (animated 0 <-> 180 on click)
  const flipMV = useMotionValue(0);
  useEffect(() => {
    const controls = animate(flipMV, isFlipped ? 180 : 0, {
      duration: 0.55,
      ease: [0.2, 0.9, 0.2, 1],
    });
    return () => controls.stop();
  }, [isFlipped, flipMV]);

  // Tilt motion values (hover + drag offsets, clamped well below 90 to avoid backface flicker)
  const tiltY = useMotionValue(0);
  const tiltX = useMotionValue(0);
  const springTiltY = useSpring(tiltY, { stiffness: 220, damping: 24 });
  const springTiltX = useSpring(tiltX, { stiffness: 220, damping: 24 });

  // Combined rotateY = flip + tilt offset. Single transform on one wrapper -> no nesting issues.
  const combinedRotateY = useTransform([flipMV, springTiltY], ([f, t]) => f + t);

  // Pointer position for parallax foil glare (0..100)
  const glareX = useMotionValue(50);
  const glareY = useMotionValue(50);
  const sGlareX = useSpring(glareX, { stiffness: 180, damping: 20 });
  const sGlareY = useSpring(glareY, { stiffness: 180, damping: 20 });

  // Idle float as motion value (no React re-renders)
  const floatY = useMotionValue(0);
  useEffect(() => {
    if (!interactive) return;
    let raf;
    const start = performance.now();
    const loop = (t) => {
      const elapsed = (t - start) / 1000;
      floatY.set(Math.sin(elapsed * 1.2) * 4);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [interactive, floatY]);

  // Clean up any pending tap timer on unmount.
  useEffect(() => () => {
    if (tapRef.current.timer) clearTimeout(tapRef.current.timer);
  }, []);

  const handleMove = (clientX, clientY) => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const xPct = ((clientX - rect.left) / rect.width) * 100;
    const yPct = ((clientY - rect.top) / rect.height) * 100;
    glareX.set(Math.max(0, Math.min(100, xPct)));
    glareY.set(Math.max(0, Math.min(100, yPct)));
    if (!dragRef.current.isDragging) {
      tiltY.set((xPct - 50) * 0.16);
      tiltX.set(-(yPct - 50) * 0.16);
    }
  };

  const onPointerDown = (e) => {
    if (!interactive) return;
    dragRef.current.isDragging = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.lastX = e.clientX;
    dragRef.current.dragged = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    handleMove(e.clientX, e.clientY);
    if (!dragRef.current.isDragging) return;
    const dx = e.clientX - dragRef.current.lastX;
    dragRef.current.lastX = e.clientX;
    if (Math.abs(e.clientX - dragRef.current.startX) > 4 || Math.abs(e.clientY - dragRef.current.startY) > 4) {
      dragRef.current.dragged = true;
    }
    const next = tiltY.get() + dx * 0.6;
    // Clamp far below 90 deg so we never accidentally cross the backface boundary via tilt
    tiltY.set(Math.max(-60, Math.min(60, next)));
  };
  const resetTilt = () => {
    tiltX.set(0);
    tiltY.set(0);
    glareX.set(50);
    glareY.set(50);
  };

  const onPointerUp = (e) => {
    const wasDragging = dragRef.current.isDragging;
    dragRef.current.isDragging = false;
    if (!interactive || !wasDragging) return;
    if (dragRef.current.dragged) return; // it was a drag-spin, not a tap

    const isTouch = e.pointerType === "touch";
    setShowHint(false);
    if (!isTouch) {
      // Mouse/pen: hover already reveals the holo, so a single click flips.
      setIsFlipped((v) => !v);
      return;
    }
    // Touch: 1 tap toggles the holo reveal, a quick 2nd tap flips the card.
    const now = Date.now();
    if (now - tapRef.current.lastTap < 300) {
      if (tapRef.current.timer) clearTimeout(tapRef.current.timer);
      tapRef.current = { lastTap: 0, timer: null };
      setIsFlipped((v) => !v);
    } else {
      tapRef.current.lastTap = now;
      tapRef.current.timer = setTimeout(() => {
        setMobileActive((v) => {
          if (v) resetTilt();
          return !v;
        });
        tapRef.current = { lastTap: 0, timer: null };
      }, 280);
    }
  };

  const onPointerLeave = () => {
    if (!dragRef.current.isDragging && !mobileActive) {
      resetTilt();
    }
  };

  const orientation = card.orientation === "landscape" ? "landscape" : "portrait";
  const SIZES = {
    portrait: {
      md: "w-[220px] h-[308px] sm:w-[240px] sm:h-[336px]",
      lg: "w-[300px] h-[420px] sm:w-[340px] sm:h-[476px]",
      xl: "w-[min(300px,80vw)] h-[min(420px,112vw)] sm:w-[420px] sm:h-[588px]",
    },
    landscape: {
      md: "w-[min(308px,82vw)] h-[min(220px,58.6vw)] sm:w-[336px] sm:h-[240px]",
      lg: "w-[min(340px,84vw)] h-[min(243px,60vw)] sm:w-[476px] sm:h-[340px]",
      xl: "w-[min(340px,84vw)] h-[min(243px,60vw)] sm:w-[588px] sm:h-[420px]",
    },
  };
  const sizeClass = (SIZES[orientation][size]) || SIZES[orientation].md;

  const glareBg = useTransform(
    [sGlareX, sGlareY],
    ([gx, gy]) => `radial-gradient(circle at ${gx}% ${gy}%, rgba(255,255,255,0.45), rgba(255,255,255,0.0) 45%)`
  );

  const cardEl = (
    <div
      ref={wrapperRef}
      data-testid="card-3d-wrapper"
      className={cn("relative perspective-card group", sizeClass)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerLeave}
      style={{ cursor: interactive ? "grab" : "default", touchAction: "none" }}
    >
      <motion.div
        data-testid="card-3d"
        className={cn(
          "card-3d card-aura relative rounded-[14px] w-full h-full",
          mobileActive && "is-active",
          RARITY_CLASS[card.rarity] || RARITY_CLASS.common
        )}
        style={{
          rotateX: springTiltX,
          rotateY: combinedRotateY,
          y: floatY,
        }}
      >
        {isLocked ? <LockedFront card={card} /> : <CardFront card={card} />}
        <CardBack card={card} />
      </motion.div>
      {interactive && showHint && (
        <div
          data-testid="card-touch-hint"
          className="absolute -bottom-7 left-1/2 -translate-x-1/2 z-20 pointer-events-none whitespace-nowrap rounded-full bg-black/70 border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-white/70 backdrop-blur-sm"
        >
          Tap: holo · Double-tap: flip · Drag: spin
        </div>
      )}
    </div>
  );

  // Wherever a player can hover a card, surface the same arena-stats popup used
  // across every list/grid view (skipped for locked cards or cards with no arena data).
  if (hoverStats && !isLocked && card.arena) {
    return <CardHoverPreview card={card} side={previewSide}>{cardEl}</CardHoverPreview>;
  }
  return cardEl;
}

export function CardGridItem({ card, onClick }) {
  const RarityClass = RARITY_CLASS[card.rarity] || RARITY_CLASS.common;
  const aspect = card.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]";
  return (
    <button
      onClick={onClick}
      data-testid={`card-grid-item-${card.id}`}
      className={cn(
        "peek-host group relative rounded-xl overflow-hidden card-aura transition-transform hover:scale-[1.025] hover:-translate-y-1 duration-200 ease-out w-full text-left bg-[hsl(220_18%_10%)]",
        aspect,
        RarityClass
      )}
    >
      {card.locked ? (
        <LockedCardArtwork card={card} view="grid" />
      ) : (
        <div className="absolute inset-0">
          {card.front_image && <img src={card.front_image} alt={card.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full peek-framed" style={cardImageVars(card)} />}
          <div className="foil-overlay peek-fade" />
          {card.rarity === "goddess" && <div className="peek-fade"><SparkleLayer count={8} /></div>}
          <div className="absolute inset-0 holo-scan peek-fade" />
        </div>
      )}

      <div className="absolute top-10 left-2 right-2 pointer-events-none"><CardLifecycleBadge card={card} /></div>
      <div className="absolute top-2 left-2 right-2 flex items-start justify-between peek-fade">
        <div className="font-mono text-sm text-white leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)] max-w-[60%] truncate font-display">
          {card.locked ? "???" : card.title}
        </div>
        <div className="flex flex-col items-end gap-1">
          {card.is_new && !card.locked && (
            <div data-testid={`card-new-badge-${card.id}`}
              className="font-mono text-[8px] font-bold uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-emerald-400 text-black shadow-[0_0_10px_rgba(52,211,153,0.75)] animate-pulse">
              New
            </div>
          )}
          <div className="font-mono text-[8px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded backdrop-blur-sm" style={{ color: "var(--label)", background: "rgba(0,0,0,0.5)", border: "1px solid var(--edge)" }}>
            {card.rarity}
          </div>
        </div>
      </div>
      <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between peek-fade">
        <div className="font-mono text-[9px] tracking-[0.2em] text-white/80 px-1.5 py-0.5 rounded backdrop-blur-sm bg-black/55">
          {card.serial}
        </div>
      </div>
    </button>
  );
}


// Reusable stacked-card visual: layered cards behind + a count badge. Used by the
// Playden (Collection) and Explore so duplicate editions collapse into one tile.
export function CardStack({ card, count = 1, onClick, badge, idKey }) {
  const isStack = count > 1;
  return (
    <div className="relative" data-testid={idKey ? `card-stack-${idKey}` : undefined}>
      {count > 2 && (
        <div className="absolute inset-0 translate-x-[10px] translate-y-[10px] rounded-xl bg-[hsl(220_18%_9%)] border border-white/10" aria-hidden />
      )}
      {isStack && (
        <div className="absolute inset-0 translate-x-[5px] translate-y-[5px] rounded-xl bg-[hsl(220_18%_12%)] border border-white/10" aria-hidden />
      )}
      <div className="relative">
        <CardGridItem card={card} onClick={onClick} />
        {isStack && (
          <div
            className="absolute -top-2 -right-2 z-10 pointer-events-none flex items-center gap-1 rounded-full bg-cyan-400 text-black font-mono text-[11px] font-bold px-2.5 py-1 shadow-[0_0_12px_rgba(34,211,238,0.5)] border border-cyan-200"
            data-testid={idKey ? `stack-count-${idKey}` : undefined}
          >
            <Layers className="h-3 w-3" /> {badge ?? count}
          </div>
        )}
      </div>
    </div>
  );
}


// Dense grid class shared by every tiny-card list view.
export const TINY_GRID = "grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3";

// Very tiny card tile: thumbnail-sized art with the full-art hover popup.
// Used by every list/menu — only the card detail page shows full-size cards.
export function TinyCard({ card, count = 1, onClick, idKey, footer, previewSide = "top" }) {
  const aspect = card.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]";
  const isStack = count > 1;
  return (
    <CardHoverPreview card={card} side={previewSide}>
      <div data-testid={idKey ? `tiny-card-${idKey}` : undefined}>
        <div className="relative">
          {isStack && (
            <div className="absolute inset-0 translate-x-[4px] translate-y-[4px] rounded-lg bg-[hsl(220_18%_12%)] border border-white/10" aria-hidden />
          )}
          <button
            onClick={onClick}
            data-testid={`tiny-card-item-${card.id}`}
            className={cn(
              "relative block w-full rounded-lg overflow-hidden card-aura bg-[hsl(220_18%_10%)] transition-transform duration-150 hover:scale-[1.06] hover:-translate-y-0.5",
              aspect,
              RARITY_CLASS[card.rarity] || RARITY_CLASS.common
            )}
          >
            {card.locked ? <LockedCardArtwork card={card} compact view="tiny" /> : card.front_image ? (
              <img
                src={card.front_image}
                alt={card.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full"
                style={cardImageStyle(card)}
                draggable={false}
              />
            ) : (
              <div className="absolute inset-0 bg-[hsl(220_18%_8%)]" />
            )}
          {!card.locked && <div className="foil-overlay" />}
          </button>
          {card.role_key && !card.locked && (
            <div className="absolute bottom-1 left-1 pointer-events-none">
              <CardRoleBadge card={card} size="sm" />
            </div>
          )}
          {isStack && (
            <div
              className="absolute -top-1.5 -right-1.5 z-10 pointer-events-none flex items-center gap-0.5 rounded-full bg-cyan-400 text-black font-mono text-[9px] font-bold px-1.5 py-0.5 shadow-[0_0_8px_rgba(34,211,238,0.5)] border border-cyan-200"
              data-testid={idKey ? `stack-count-${idKey}` : undefined}
            >
              <Layers className="h-2.5 w-2.5" /> {count}
            </div>
          )}
        </div>
        {footer !== undefined ? footer : (
          <div className="mt-1 px-0.5 font-mono text-[9px] text-white/55 truncate">
            {card.locked ? (card.serial || "???") : card.title}
          </div>
        )}
        <CardLifecycleBadge card={card} />
      </div>
    </CardHoverPreview>
  );
}


// Reusable hover-to-preview: wrap any element; hovering pops up the full card art
// (same behavior as the editions stack). Renders in a portal so it never clips.
const RARITY_HEX = { common: "#9ca3af", uncommon: "#34d399", rare: "#38bdf8", epic: "#c084fc", legendary: "#fbbf24", goddess: "#f472b6" };

export function CardPreviewContent({ card, rc, locked, landscape, isOwner, action = null }) {
  return (
    <>
      {/* rarity accent rail */}
      <div className="h-[3px] w-full" style={{ background: `linear-gradient(90deg, transparent, ${rc}, transparent)` }} />
      <div className="p-4">
        {/* Big name header */}
        {!locked && (
          <div className="mb-3.5 flex items-end justify-between gap-4 border-b border-white/10 pb-3">
            <div className="min-w-0">
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.4em]" style={{ color: rc }}>{card.rarity}</div>
              <h3 className="font-display text-2xl sm:text-[28px] leading-none tracking-wide text-white truncate" style={{ textShadow: `0 2px 18px ${rc}66` }}>{card.title || "Card"}</h3>
            </div>
            {card.level != null && (
              <span className="font-mono text-xs font-bold text-amber-100 px-2.5 py-1 rounded-lg bg-amber-400/10 border border-amber-400/40 shrink-0 flex items-center gap-1" data-testid={`card-hover-level-${card.id}`}>
                Lv {card.level}{card.evolution > 0 ? <span className="text-fuchsia-300">★{card.evolution}</span> : null}
              </span>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-4 items-stretch">
          <div className={cn("relative rounded-xl overflow-hidden bg-black shrink-0", landscape ? "w-[min(360px,calc(100vw-4rem))] aspect-[3.5/2.5]" : "w-[min(248px,calc(100vw-4rem))] aspect-[2.5/3.5]", RARITY_CLASS[card.rarity] || RARITY_CLASS.common)}>
            {locked ? <LockedCardArtwork card={card} view="preview" /> : card.front_image ? (
              <img
                src={card.front_image}
                alt={card.title}
                className="absolute inset-0 w-full h-full object-contain"
                draggable={false}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/40">No image</div>
            )}
            {!locked && <div className="foil-overlay" />}
            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-white/90 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-sm truncate">{card.serial}</span>
              {card.edition_number != null && <span className="font-mono text-[11px] text-cyan-200 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-sm shrink-0">#{card.edition_number}</span>}
            </div>
          </div>
          {!locked && card.arena && (
            <div className="w-full sm:w-[360px] min-w-0" data-testid={`card-hover-arena-${card.id}`}>
              <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-cyan-300/80 mb-2">Arena Profile</div>
              <ArenaProfile arena={card.arena} card={card} interactive={isOwner} />
              {((card.gear || []).length > 0 || (card.loadout || []).length > 0) && (
                <div className="mt-3 pt-3 border-t border-white/10" data-testid={`card-hover-gear-${card.id}`}>
                  <GearSlots gear={card.gear} loadout={card.loadout} />
                </div>
              )}
            </div>
          )}
        </div>
        {action && <div className="mt-3 pt-3 border-t border-white/10">{action}</div>}
      </div>
    </>
  );
}

export function CardHoverPreview({ card, children, side = "right", align = "center", action = null }) {
  // Hooks must run unconditionally — declare BEFORE any early return.
  const [open, setOpen] = useState(false);
  const tappedElRef = useRef(null);   // element under the opening tap (touch)
  const replayingRef = useRef(false); // true while we re-fire that tap's click
  const { user } = useAuth() || {};
  const isOwner = !!(user && card && card.owner_id === user.id && !card.locked);
  if (!card) return children;
  const landscape = card.orientation === "landscape";
  const locked = card.locked === true;
  const rc = RARITY_HEX[card.rarity] || RARITY_HEX.common;
  const touch = typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(hover: none)").matches;

  // On TOUCH devices a side-anchored popover frequently lands off-screen on a
  // narrow phone (the user taps a card and "nothing" appears). So on touch we
  // show the preview as a CENTERED overlay the player can actually see. The
  // opening tap is swallowed in the capture phase so the card's real action
  // (navigate to detail, select, open stack…) doesn't fire on a peek; we
  // remember the exact element tapped so a "Continue" button can REPLAY that
  // click once the player has looked. Tapping the backdrop or ✕ just closes.
  // Mouse users keep the classic hover-to-preview behavior below.
  if (touch) {
    const continueAction = () => {
      const el = tappedElRef.current;
      setOpen(false);
      if (el && el.isConnected) {
        replayingRef.current = true;
        el.click();
        replayingRef.current = false;
      }
    };
    return (
      <>
        <span style={{ display: "contents" }}
          onClickCapture={(e) => {
            if (replayingRef.current) return; // let the replayed click through
            tappedElRef.current = e.target.closest("a,button,[role='button']") || e.target;
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}>
          {children}
        </span>
        {open && createPortal(
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-3"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }} data-testid={`card-hover-preview-${card.id}`}>
            <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto overscroll-contain rounded-2xl border border-white/15 bg-[hsl(222_30%_6%)]/95 ring-1 ring-white/5"
              style={{ boxShadow: `0 40px 120px -24px rgba(0,0,0,0.95), 0 0 0 1px ${rc}22, 0 0 40px -18px ${rc}` }}
              onClick={(e) => e.stopPropagation()}>
              <button onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                data-testid={`card-hover-preview-close-${card.id}`} aria-label="Close preview"
                className="absolute top-2.5 right-2.5 z-10 rounded-full bg-black/60 border border-white/15 p-1.5 text-white/70 hover:text-white">
                <X className="h-4 w-4" />
              </button>
              <CardPreviewContent card={card} rc={rc} locked={locked} landscape={landscape} isOwner={isOwner} action={action} />
              {!action && (
                <div className="sticky bottom-0 px-4 pt-3 pb-4 bg-[hsl(222_30%_6%)]/95 backdrop-blur-sm border-t border-white/10">
                  <button
                    onClick={(e) => { e.stopPropagation(); continueAction(); }}
                    data-testid={`card-hover-preview-select-${card.id}`}
                    className="w-full rounded-xl bg-cyan-400 text-black font-semibold py-2.5 hover:bg-cyan-300 transition-colors">
                    Continue
                  </button>
                </div>
              )}
            </div>
          </div>, document.body)}
      </>
    );
  }

  return (
    <HoverCard openDelay={100} closeDelay={120} open={open} onOpenChange={setOpen}>
      <HoverCardTrigger asChild onClick={() => setOpen((o) => !o)}>{children}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        align={align}
        sideOffset={14}
        collisionPadding={12}
        avoidCollisions
        sticky="always"
        data-testid={`card-hover-preview-${card.id}`}
        className="z-[200] w-auto max-w-[92vw] max-h-[var(--radix-hover-card-content-available-height,88vh)] overflow-y-auto overflow-x-hidden overscroll-contain rounded-2xl border border-white/15 bg-[hsl(222_30%_6%)]/95 p-0 shadow-[0_40px_120px_-24px_rgba(0,0,0,0.95)] ring-1 ring-white/5 backdrop-blur-2xl"
        style={{ boxShadow: `0 40px 120px -24px rgba(0,0,0,0.95), 0 0 0 1px ${rc}22, 0 0 40px -18px ${rc}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <CardPreviewContent card={card} rc={rc} locked={locked} landscape={landscape} isOwner={isOwner} action={action} />
      </HoverCardContent>
    </HoverCard>
  );
}

